import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, TableListActions, WithInfoIcon, Tooltip } from 'seagull';
import { Coins, Eye, InvoiceEdit } from 'seagull/icons';
import { fromRelationshipFundingSource } from '~/business-logic/paymentMethodCellLogic';
import { useAgreementRowMenuItems } from '~/components/AgreementActions/AgreementRowMenuItems';
import EnterAccountantAccessButton from '~/components/EnterAccountantAccessButton/EnterAccountantAccessButton';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, relationshipSummaryModel, timeModel } from '~/models';
import styles from './AgreementsDashboardRow.module.scss';
export const agreementStatusLabelsTextsPath = 'pages.agreementsDashboard.statusLabels';
function getAgreementStatusLabel(status, texts) {
    switch(status){
        case relationshipSummaryModel.AgreementStatus.Active:
            return texts.active();
        case relationshipSummaryModel.AgreementStatus.Exhausted:
            return texts.completed();
        case relationshipSummaryModel.AgreementStatus.Terminated:
            return texts.terminated();
        default:
            return status;
    }
}
export default function AgreementsDashboardRow(props) {
    var _relationshipSummaryModel_agreementStatus;
    const { isVendor, index, hasAccountantAccess, onVerifyPaymentMethod, onEditAgreement, onDownloadPDF, onDuplicateAgreement, onTerminateAgreement, onEnterAccountantAccess, onRenameAgreement, onChangeAssignee } = props;
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE } = useFeatureFlags();
    const { t: dashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const { t: rowTooltipTexts } = useLocaleKeys('pages.agreementsDashboard.rowTooltips');
    const { t: rowActionTexts } = useLocaleKeys('pages.agreementsDashboard.rowActions');
    const { t: agreementStatusLabelsTexts } = useLocaleKeys(agreementStatusLabelsTextsPath);
    const { t: paymentMethodTexts } = useLocaleKeys('common.paymentMethod');
    const [agreement, setAgreement] = useState(props.agreement);
    useEffect(()=>setAgreement(props.agreement), [
        props.agreement
    ]);
    const { native } = useIoContext();
    const history = useHistory();
    const displayedTitle = relationshipSummaryModel.displayedTitle(agreement, isVendor);
    const testId = agreement.isSample ? 'test-agreement' : "agreements-row-".concat(index);
    const heapId = agreement.isSample ? 'test-agreement' : 'agreement';
    const isTerminated = relationshipSummaryModel.isTerminated(agreement);
    const canCompleteVerification = relationshipSummaryModel.canCompleteVerification(agreement);
    const agreementStatus = (_relationshipSummaryModel_agreementStatus = relationshipSummaryModel.agreementStatus(agreement)) !== null && _relationshipSummaryModel_agreementStatus !== void 0 ? _relationshipSummaryModel_agreementStatus : '';
    const canRequestPM = (!agreement.fundingSource || agreement.fundingSource.failedVerification()) && !relationshipSummaryModel.isTerminated(agreement) && isVendor;
    const agreementLink = isVendor || !relationshipModel.isPending(agreement) ? routes.agreementMinisiteRoutes(agreement.id).agreement : routes.clientOnboarding(agreement.id);
    const items = compact([
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.title,
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        ellipsis: true,
                        hideTooltip: true,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                weight: "normal",
                                testId: "".concat(testId, "-company-name"),
                                tooltipTestId: "".concat(testId, "-company-name-tooltip"),
                                children: relationshipSummaryModel.opposingPartyName(agreement, isVendor)
                            }),
                            displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: "".concat(testId, "-title"),
                                tooltipTestId: "".concat(testId, "-title-tooltip"),
                                children: displayedTitle
                            })
                        ]
                    }),
                    !ENABLE_KILL_ACCOUNTANT_ACCESS_FE && hasAccountantAccess && /*#__PURE__*/ _jsx(EnterAccountantAccessButton, {
                        testId: "".concat(testId, "-enter-acc-access"),
                        onClick: (e)=>{
                            e.stopPropagation();
                            onEnterAccountantAccess(relationshipSummaryModel.customerBusinessId(agreement), relationshipSummaryModel.customerBusinessName(agreement));
                        }
                    })
                ]
            })
        },
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.status,
                children: [
                    /*#__PURE__*/ _jsx(WithInfoIcon, {
                        showInfo: agreementStatus === relationshipSummaryModel.AgreementStatus.Exhausted,
                        info: rowActionTexts.allServicesCompleted(),
                        infoTestId: "".concat(testId, "-status-info"),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            testId: "".concat(testId, "-status"),
                            children: getAgreementStatusLabel(agreementStatus, agreementStatusLabelsTexts)
                        })
                    }),
                    agreement.hasAmendments && /*#__PURE__*/ _jsx(Tooltip, {
                        placement: "top",
                        title: rowTooltipTexts.awaitingApproval(),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            testId: "".concat(testId, "-has-amendments"),
                            children: /*#__PURE__*/ _jsx(InvoiceEdit, {
                                size: 16
                            })
                        })
                    })
                ]
            })
        },
        isVendor && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: "".concat(testId, "-assignee"),
                children: relationshipSummaryModel.sellerFullName(agreement)
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                children: agreement.effectiveDate ? timeModel.formatDateLong(agreement.effectiveDate) : ''
            })
        },
        {
            value: (()=>{
                var _agreement_fundingSource, _agreement_fundingSource1;
                const cellState = fromRelationshipFundingSource(agreement.fundingSource);
                if (cellState.kind === 'notSet') {
                    return /*#__PURE__*/ _jsx(Typography, {
                        skin: "error",
                        children: dashboardTexts.notSet()
                    });
                }
                if (cellState.kind === 'pendingDetails') {
                    return /*#__PURE__*/ _jsx(WithInfoIcon, {
                        info: paymentMethodTexts.pendingDetailsTooltip(),
                        showInfo: true,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: paymentMethodTexts.pendingDetails()
                        })
                    });
                }
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            skin: "secondary",
                            children: [
                                cellState.type === 'creditCard' ? 'Card' : 'Bank account',
                                " (",
                                cellState.last4,
                                ")"
                            ]
                        }),
                        ((_agreement_fundingSource = agreement.fundingSource) === null || _agreement_fundingSource === void 0 ? void 0 : _agreement_fundingSource.failedVerification()) && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "error",
                            children: dashboardTexts.verificationFailed()
                        }),
                        ((_agreement_fundingSource1 = agreement.fundingSource) === null || _agreement_fundingSource1 === void 0 ? void 0 : _agreement_fundingSource1.isPendingMicroDeposit()) && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "warning",
                            children: agreement.fundingSource.isPendingAutomaticVerification() ? 'Automatic verification initiated' : 'Manual verification initiated'
                        })
                    ]
                });
            })()
        },
        {
            value: agreement.lastBilledDate ? /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: "".concat(testId, "-last-billed"),
                children: timeModel.formatDateLong(agreement.lastBilledDate)
            }) : null
        }
    ]);
    const getButtonText = ()=>{
        if (canCompleteVerification) return rowActionTexts.completeVerification();
        return canRequestPM ? rowActionTexts.addPaymentMethod() : undefined;
    };
    const onButtonClick = ()=>{
        if (canCompleteVerification) {
            onVerifyPaymentMethod(agreement);
        } else {
            history.push(routes.agreementMinisiteRoutes(agreement.id).paymentMethods);
        }
    };
    const menuRow = {
        id: agreement.id,
        draftId: agreement.draftId,
        isSample: agreement.isSample,
        customerName: relationshipSummaryModel.customerBusinessName(agreement),
        customerBusinessId: relationshipSummaryModel.customerBusinessId(agreement),
        status: agreement.status
    };
    const menuItems = useAgreementRowMenuItems({
        agreement: menuRow,
        isVendor,
        isTerminated,
        testId,
        heapId,
        onEditAgreement,
        onRenameAgreement,
        onRenameSuccess: (title)=>setAgreement(relationshipSummaryModel.setTitle(agreement, title)),
        onChangeAssignee,
        onDuplicateAgreement,
        onDownloadPDF,
        onTerminateAgreement
    });
    return /*#__PURE__*/ _jsx(TableList.Row, {
        onClick: ()=>history.push(agreementLink),
        items: items,
        rowId: agreement.id,
        testId: testId,
        "data-heapid": heapId,
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            testId: testId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            buttonText: getButtonText(),
            buttonIcon: /*#__PURE__*/ _jsx(Coins, {}),
            secondaryButtonText: native.isNative ? undefined : rowActionTexts.view(),
            secondaryBtnHeapId: "".concat(heapId, "-view-btn"),
            secondaryButtonIcon: native.isNative ? undefined : /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: onButtonClick,
            onClickSecondaryButton: native.isNative ? undefined : ()=>history.push(agreementLink),
            mobile: {
                title: relationshipSummaryModel.opposingPartyName(agreement, isVendor),
                subtitle: displayedTitle !== null && displayedTitle !== void 0 ? displayedTitle : undefined
            },
            children: menuItems.length > 0 && menuItems
        })
    });
}
